"use client";

import { useMemo, ReactNode } from "react";
import { withErrorBoundary } from "@sentry/nextjs";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { MAP_AREA } from "@/constant";
import { PropertyTypeV2 } from "commonModule/services/common";
import ChevronLeftIcon from "components/icons-svg/ChevronLeftIcon";
import { selectBookingStore } from "stores/booking/bookingSlice";
import { useAppSelector } from "stores/hooks";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { getLastSearchUrl } from "utils/local-storage";
import { buildMultiUnitDetailPageUrl, createSearchURL, ensembleCityStateCountry, getQueryParams } from "utils/url";
import DetailPageActionButtons from "./DetailPageActionButtons";
const isVacationRentalsUrl = (urlOrPath: string) => {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://whimstay.com";
    const u = new URL(urlOrPath, base);
    return u.pathname.startsWith("/vacation-rentals");
  } catch {
    return false;
  }
};
const DetailPageTopBar = ({
  coverImg,
  property,
  multiUnitGrpId
}: {
  coverImg: string;
  property: PropertyTypeV2;
  multiUnitGrpId?: string | null;
}) => {
  const pathname = usePathname();
  const params = useSearchParams();
  const bookingStore = useAppSelector(selectBookingStore);
  const isgoogle = params?.get("isgoogle") === "true";
  // const name = params?.get("name")?.trim()?.toLowerCase() || ""
  const [islargerThan768] = useMediaQuery("(min-width: 768px)");
  // const locationName = useMemo(() => {
  //   const splitName = (name !== MAP_AREA ? name : "")
  //     .split(",")
  //     .map((x) => x.trim())
  //   return splitName[0]?.charAt(0).toUpperCase() + splitName[0]?.slice(1) || ""
  // }, [name])
  const isMultiUnitDetailPage = pathname?.startsWith("/multi-unit-detail") || false;

  // const track = () => {
  //   localStorage.setItem("searchPageOrigin", "property detail page - top")
  // }

  const BackToSearchLinksContent = ({
    children,
    className = "",
    target = "_self"
  }: {
    children: ReactNode;
    className?: string;
    target?: string;
  }) => {
    const fromMultiUnitProperty = params?.get("fromMultiUnit");
    const formattedLocationName = ensembleCityStateCountry({
      city: property.city,
      state: property.state,
      country: property.country
    });
    const multiUnitPropertyUrl = useMemo(() => buildMultiUnitDetailPageUrl({
      name: (params?.get("name")?.trim()?.toLowerCase() !== MAP_AREA ? params?.get("name")?.trim()?.toLowerCase() : "") || " ",
      propertyId: fromMultiUnitProperty || property.property_id,
      propertyName: property?.property_sub_type_desc || property.property_name,
      multiUnitGroupId: multiUnitGrpId || "",
      startDate: params?.get("check_in") || "",
      endDate: params?.get("check_out") || "",
      adultCount: params?.get("adultsCount") || 0,
      childrenCount: params?.get("childrenCount") || 0,
      petAllowed: params?.get("suitablePet") === "true",
      cancellable: params?.get("cancellable") === "true",
      page: params?.get("page") || ""
    }), [fromMultiUnitProperty]);
    const href = fromMultiUnitProperty && multiUnitGrpId ? multiUnitPropertyUrl : `/vacation-rentals/${createSearchURL(params?.get("name")?.trim()?.toLowerCase() !== MAP_AREA ? params?.get("name")?.trim()?.toLowerCase() || formattedLocationName : formattedLocationName)}${getQueryParams(bookingStore, params
    // property
    )}`;
    const backHref = useMemo(() => {
      // Only override on same-tab navigation.
      if (target !== "_self") return href;
      const lastSearchUrl = getLastSearchUrl() || "";
      return lastSearchUrl && isVacationRentalsUrl(lastSearchUrl) ? lastSearchUrl : href;
    }, [href, target]);
    return <Link href={backHref} target={target} className={className} rel='noreferrer' id='backToSearchLink' data-sentry-element="Link" data-sentry-component="BackToSearchLinksContent" data-sentry-source-file="DetailPageTopBar.tsx">
        {children}
      </Link>;
  };
  return <div className='flex items-center justify-between gap-4 px-4 text-white md:bg-transparent md:px-0 md:pb-4 md:pt-6' data-sentry-component="DetailPageTopBar" data-sentry-source-file="DetailPageTopBar.tsx">
      {/* only show mobile screen */}
      {!isgoogle && <div className='relative block md:hidden'>
          <div className='absolute left-0 top-3 z-[1]'>
            <BackToSearchLinksContent className='relative flex h-10 w-10 items-center justify-center rounded-full border-none bg-[#00000080] text-white after:pointer-events-auto after:absolute after:-inset-1 after:rounded-full after:bg-transparent after:content-[""]'>
              <ChevronLeftIcon title='multiUnitPropertyChevronLeftIconTitle' className={`mr-1 h-3.5 w-3.5 stroke-1 text-white`} />
            </BackToSearchLinksContent>
          </div>
        </div>}

      {/* only show mobile screen */}
      {isgoogle && !isMultiUnitDetailPage && <div className='relative block md:hidden'>
          <div className='absolute left-0 top-3 z-[1]'>
            <BackToSearchLinksContent className={`relative flex h-10 w-10 items-center justify-center whitespace-nowrap rounded-[20px] rounded-full border-none bg-[#00000080] text-[13px] font-semibold text-white after:pointer-events-auto after:absolute after:-inset-1 after:rounded-[20px] after:bg-transparent after:content-[""]`} target='_blank'>
              <ChevronLeftIcon title='propertyChevronLeftIconTitle' className='h-[14px] w-[14px] stroke-1 text-white' />
            </BackToSearchLinksContent>
          </div>
        </div>}

      {/* only show desktop and tablet screen */}
      <div>
        {isgoogle && !isMultiUnitDetailPage && <p className='line-clamp-1 hidden text-sm font-medium leading-6 text-white md:block md:text-[#61646A]'>
            <BackToSearchLinksContent className='flex items-center underline' target='_blank'>
              <ChevronLeftIcon title='propertyChevronLeftIconTitle' className={`mr-2 h-3 w-3 stroke-1 text-textColor md:text-textColor`} />
              {`Explore similar deals in ${property.city?.length > 0 ? property.city : property.state?.length > 0 ? property.state : "USA"}`}
            </BackToSearchLinksContent>
          </p>}
        {/* only show desktop and tablet screen */}
        {!isgoogle && <p className='hidden bg-white text-sm font-medium text-[#61646A] md:block'>
            <BackToSearchLinksContent className='flex items-center gap-1'>
              <ChevronLeftIcon title='viewAllPropertiesChevronLeftIconTitle' className={`mr-2 h-3 w-3 stroke-1 text-textColor`} />
              {/* View all properties in {property.city || "USA"} */}
              Back to search results
            </BackToSearchLinksContent>
          </p>}
      </div>
      <div className='relative md:h-8'>
        <div className={`absolute right-0 ${isgoogle ? "top-16" : "top-4"} z-[1] flex items-center gap-2 md:bottom-0 md:right-0 md:top-0 md:gap-4`}>
          {islargerThan768 && <DetailPageActionButtons property={property} coverImg={coverImg} isMultiUnitDetailPage={isMultiUnitDetailPage} />}
        </div>
      </div>
    </div>;
};
export default withErrorBoundary(DetailPageTopBar, {
  beforeCapture: scope => {
    scope.setTag("ws_location", "DetailPageTopBar");
  }
});